import React, { useCallback, useMemo, useState } from 'react';
import { patientAPI, reportsAPI } from '../../services/api';
import { usePrompt, useToast } from '../UIProvider';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import useDrill from './shared/useDrill';
import MergePatientsDialog from './MergePatientsDialog';
import { formatPatientAge } from '../../utils/age';
import { formatClinicDateTime } from '../../utils/clinicTime';
import { lookalikeRuleLabel, lookalikeRulesOf } from '../../constants/lookalikeRules';

// Pairs of patient records that look like the same person registered twice. Matching is
// deterministic: names are normalised (punctuation dropped, Muhammad spellings folded, tokens
// sorted so a reversed name still matches), phones are reduced to their last 10 digits, and CNICs
// are compared both whole and by their first 12 digits, since the 13th is a gender digit that no
// two people share. A birth date the family estimated is matched as a year-wide band.
//
// Nothing is auto-merged and nothing is hidden by the system. A pair is ranked, and anything that
// argues against it (a different CNIC, a different gender) is shown next to it so a weak match can
// be dismissed without opening either record. A person can clear a pair as two different people;
// that decision is listed at the bottom and can be undone.
//
// The section at the top is the feedback loop: every registration that went ahead after the form
// warned, judged again under the same rules as the files stand today.

const TIERS = {
  4: { label: 'Confirmed', chip: 'bg-rose-100 text-rose-700' },
  3: { label: 'High', chip: 'bg-amber-100 text-amber-800' },
  2: { label: 'Medium', chip: 'bg-sky-100 text-sky-700' },
  1: { label: 'Low', chip: 'bg-gray-100 text-gray-600' },
};

// How sure the registration form was when it warned.
const STRENGTHS = {
  CERTAIN: { label: 'Certain', chip: 'bg-rose-100 text-rose-700' },
  LIKELY: { label: 'Likely', chip: 'bg-amber-100 text-amber-800' },
  POSSIBLE: { label: 'Possible', chip: 'bg-sky-100 text-sky-700' },
};

const FILTERS = [
  { key: 'all', label: 'All', match: () => true },
  { key: '4', label: 'Confirmed', match: (r) => Number(r.tier) === 4 },
  { key: '3', label: 'High', match: (r) => Number(r.tier) === 3 },
  { key: '2', label: 'Medium', match: (r) => Number(r.tier) === 2 },
  { key: '1', label: 'Low', match: (r) => Number(r.tier) === 1 },
];

const tierOf = (row) => TIERS[Number(row.tier)] || TIERS[1];

// Strongest rule first, so the chip that justifies the confidence badge leads.
const rulesOf = lookalikeRulesOf;
const ruleLabel = lookalikeRuleLabel;

const Chip = ({ className, children }) => (
  <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${className}`}>{children}</span>
);

const RuleChips = ({ row }) => (
  <span className="flex flex-wrap gap-1">
    {rulesOf(row).map((code) => (
      <Chip key={code} className="bg-gray-100 text-gray-700">{ruleLabel(code)}</Chip>
    ))}
  </span>
);

const PatientCell = ({ name, mrn, status }) => (
  <div className="leading-tight">
    <div className="font-medium text-gray-900">{name}</div>
    <div className="text-xs text-gray-500 font-mono">
      {mrn}
      {status && status !== 'ACTIVE' && <span className="ml-1.5 text-rose-600 font-sans">{status}</span>}
    </div>
  </div>
);

// "about 6 years, female" for an estimated date, "6 years, female" for a known one.
const ageGender = (person, gender) =>
  `${formatPatientAge(person) || '—'}, ${(gender || 'not recorded').toLowerCase()}`;
const history = (visits, lastVisit) =>
  Number(visits) === 0 ? 'No visits' : `${visits} visits, last ${lastVisit || 'unknown'}`;
const when = (value) => formatClinicDateTime(value);

// Every column here carries a "Primary · " / "Candidate · " prefix, so the headings alone are wider
// than most of the values. The free-text ones wrap so fourteen columns still land inside the window.
const side = (prefix, heading) => [
  {
    key: `${prefix}Patient`, label: `${heading} · Patient`, wrap: true,
    render: (r) => <PatientCell name={r[`${prefix}Patient`]} mrn={r[`${prefix}Mrn`]} status={r[`${prefix}Status`]} />,
    csv: (r) => `${r[`${prefix}Patient`]} (${r[`${prefix}Mrn`]})`,
    sortValue: (r) => r[`${prefix}Patient`],
  },
  {
    key: `${prefix}DateOfBirth`, label: `${heading} · Age / Gender`, wrap: true,
    render: (r) => ageGender({ dateOfBirth: r[`${prefix}DateOfBirth`], dobEstimated: r[`${prefix}DobEstimated`] }, r[`${prefix}Gender`]),
    csv: (r) => ageGender({ dateOfBirth: r[`${prefix}DateOfBirth`], dobEstimated: r[`${prefix}DobEstimated`] }, r[`${prefix}Gender`]),
  },
  {
    key: `${prefix}Cnic`, label: `${heading} · CNIC`,
    render: (r) => <span className="font-mono text-xs">{r[`${prefix}Cnic`] || '—'}</span>,
  },
  {
    key: `${prefix}Phone`, label: `${heading} · Phone`,
    render: (r) => <span className="font-mono text-xs">{r[`${prefix}Phone`]}</span>,
  },
  {
    key: `${prefix}Visits`, label: `${heading} · History`, wrap: true,
    render: (r) => history(r[`${prefix}Visits`], r[`${prefix}LastVisit`]),
    csv: (r) => history(r[`${prefix}Visits`], r[`${prefix}LastVisit`]),
    sortValue: (r) => Number(r[`${prefix}Visits`]),
  },
];

const COLUMNS = [
  {
    key: 'tier', label: 'Confidence',
    render: (r) => <Chip className={tierOf(r).chip}>{tierOf(r).label}</Chip>,
    csv: (r) => tierOf(r).label,
    sortValue: (r) => Number(r.tier),
  },
  {
    key: 'rules', label: 'Matched On', wrap: true,
    render: (r) => <RuleChips row={r} />,
    csv: (r) => rulesOf(r).map(ruleLabel).join('; '),
  },
  {
    // Facts that argue the two are different people. A CNIC is unique per person, so a pair with
    // two different ones is almost certainly a false positive and can be cleared immediately.
    key: 'conflicts', label: 'Conflicts',
    render: (r) => (r.conflicts ? <Chip className="bg-rose-50 text-rose-700">{r.conflicts}</Chip> : <span className="text-gray-300">none</span>),
    csv: (r) => r.conflicts || '',
  },
  ...side('a', 'Primary'),
  ...side('b', 'Candidate'),
  {
    key: 'differs', label: 'Differs On', wrap: true,
    render: (r) => <span className="text-gray-500">{r.differs || 'nothing'}</span>,
    csv: (r) => r.differs || '',
  },
];

// What a warned pair looks like today under the same rules, so the section says whether the
// warning was right rather than only that it was given.
const nowOf = (r) => {
  if (r.cleared) return { label: 'Cleared: two people', chip: 'bg-emerald-100 text-emerald-800' };
  if (r.newDeleted || r.matchDeleted || r.tierNow == null) return { label: 'A file was removed', chip: 'bg-gray-100 text-gray-600' };
  const tier = Number(r.tierNow);
  if (tier === 0) return { label: 'No longer matches', chip: 'bg-gray-100 text-gray-600' };
  return { label: `${TIERS[tier].label} match today`, chip: TIERS[tier].chip };
};
const strengthOf = (r) => STRENGTHS[r.strength] || STRENGTHS.POSSIBLE;

const WARNED_COLUMNS = [
  {
    key: 'decidedAt', label: 'When',
    render: (r) => when(r.decidedAt), csv: (r) => when(r.decidedAt), sortValue: (r) => r.decidedAt || '',
  },
  {
    key: 'newPatient', label: 'Registered', wrap: true,
    render: (r) => <PatientCell name={r.newPatient} mrn={r.newMrn} status={r.newDeleted ? 'DELETED' : r.newStatus} />,
    csv: (r) => `${r.newPatient} (${r.newMrn})`,
    sortValue: (r) => r.newPatient,
  },
  {
    key: 'newDateOfBirth', label: 'Age / Gender', wrap: true,
    render: (r) => ageGender({ dateOfBirth: r.newDateOfBirth, dobEstimated: r.newDobEstimated }, r.newGender),
    csv: (r) => ageGender({ dateOfBirth: r.newDateOfBirth, dobEstimated: r.newDobEstimated }, r.newGender),
  },
  {
    key: 'matchPatient', label: 'Looked Like', wrap: true,
    render: (r) => <PatientCell name={r.matchPatient} mrn={r.matchMrn} status={r.matchDeleted ? 'DELETED' : r.matchStatus} />,
    csv: (r) => `${r.matchPatient} (${r.matchMrn})`,
    sortValue: (r) => r.matchPatient,
  },
  {
    key: 'strength', label: 'Warning',
    render: (r) => <Chip className={strengthOf(r).chip}>{strengthOf(r).label}</Chip>,
    csv: (r) => strengthOf(r).label,
  },
  {
    key: 'rules', label: 'Matched On', wrap: true,
    render: (r) => <RuleChips row={r} />,
    csv: (r) => rulesOf(r).map(ruleLabel).join('; '),
  },
  { key: 'decidedBy', label: 'By', render: (r) => r.decidedBy || '—' },
  {
    key: 'tierNow', label: 'Today',
    render: (r) => <Chip className={nowOf(r).chip}>{nowOf(r).label}</Chip>,
    csv: (r) => nowOf(r).label,
    sortValue: (r) => (r.cleared ? -1 : Number(r.tierNow ?? -2)),
  },
];

const CLEARED_COLUMNS = [
  {
    key: 'decidedAt', label: 'When',
    render: (r) => when(r.decidedAt), csv: (r) => when(r.decidedAt), sortValue: (r) => r.decidedAt || '',
  },
  {
    key: 'aPatient', label: 'Patient', wrap: true,
    render: (r) => <PatientCell name={r.aPatient} mrn={r.aMrn} status={r.aDeleted ? 'DELETED' : undefined} />,
    csv: (r) => `${r.aPatient} (${r.aMrn})`,
  },
  {
    key: 'bPatient', label: 'Not The Same As', wrap: true,
    render: (r) => <PatientCell name={r.bPatient} mrn={r.bMrn} status={r.bDeleted ? 'DELETED' : undefined} />,
    csv: (r) => `${r.bPatient} (${r.bMrn})`,
  },
  {
    key: 'note', label: 'Why', wrap: true,
    render: (r) => r.note || <span className="text-gray-300">no note</span>,
    csv: (r) => r.note || '',
  },
  { key: 'decidedBy', label: 'By', render: (r) => r.decidedBy || '—' },
];

// What a merge moved across, in words, for the log at the bottom.
const moved = (r) =>
  [
    [r.visits, 'visit', 'visits'], [r.prescriptions, 'prescription', 'prescriptions'], [r.invoices, 'bill', 'bills'],
    [r.alerts, 'alert', 'alerts'], [r.notes, 'note', 'notes'], [r.creditEntries, 'credit entry', 'credit entries'],
    [r.documents, 'document', 'documents'], [r.guardians, 'guardian', 'guardians'],
    [r.histories, 'medical history', 'medical histories'],
  ]
    .filter(([n]) => Number(n) > 0)
    .map(([n, one, many]) => `${n} ${Number(n) === 1 ? one : many}`)
    .join(', ') || 'nothing on it';

const MERGED_COLUMNS = [
  {
    key: 'mergedAt', label: 'When',
    render: (r) => when(r.mergedAt), csv: (r) => when(r.mergedAt), sortValue: (r) => r.mergedAt || '',
  },
  {
    key: 'keptPatient', label: 'Kept', wrap: true,
    render: (r) => <PatientCell name={r.keptPatient} mrn={r.keptMrn} />,
    csv: (r) => `${r.keptPatient} (${r.keptMrn})`,
  },
  {
    key: 'mergedPatient', label: 'Retired', wrap: true,
    render: (r) => <PatientCell name={r.mergedPatient} mrn={r.mergedMrn} />,
    csv: (r) => `${r.mergedPatient} (${r.mergedMrn})`,
  },
  { key: 'visits', label: 'Moved', wrap: true, render: (r) => moved(r), csv: (r) => moved(r) },
  {
    key: 'fieldsFilled', label: 'Filled In', wrap: true,
    render: (r) => (r.fieldsFilled ? r.fieldsFilled.split(',').join(', ') : <span className="text-gray-300">nothing</span>),
    csv: (r) => r.fieldsFilled || '',
  },
  {
    key: 'note', label: 'Why', wrap: true,
    render: (r) => r.note || <span className="text-gray-300">no note</span>,
    csv: (r) => r.note || '',
  },
  { key: 'mergedBy', label: 'By', render: (r) => r.mergedBy || '—' },
];

// Second section. A shared mobile is normal (a household registers on one number), so this is not
// a duplicate signal on its own. It is ranked by what makes a group odd: the same name appearing
// twice under one number, then sheer group size, which is how placeholder and staff numbers show up.
const MOBILE_COLUMNS = [
  {
    key: 'phone', label: 'Mobile',
    render: (r) => <span className="font-mono">{r.phone}</span>,
  },
  { key: 'patients', label: 'Records', align: 'right' },
  {
    key: 'distinctNames', label: 'Names',
    render: (r) =>
      Number(r.distinctNames) < Number(r.patients) ? (
        <Chip className="bg-rose-100 text-rose-700">
          {r.distinctNames} names on {r.patients} records
        </Chip>
      ) : (
        <span className="text-gray-500">{r.distinctNames} different names</span>
      ),
    csv: (r) => `${r.distinctNames} names on ${r.patients} records`,
    sortValue: (r) => Number(r.distinctNames) - Number(r.patients),
  },
  { key: 'mrn', label: 'MRN', render: (r) => <span className="font-mono text-xs">{r.mrn}</span> },
  // The two name columns are the widest things here and the only ones that read fine broken over
  // two lines, so they are what gives the other eight room to fit without a sideways scroll.
  { key: 'patient', label: 'Patient', wrap: true },
  {
    key: 'dateOfBirth', label: 'Age / Gender',
    render: (r) => ageGender({ dateOfBirth: r.dateOfBirth }, r.gender),
    csv: (r) => ageGender({ dateOfBirth: r.dateOfBirth }, r.gender),
  },
  { key: 'cnic', label: 'CNIC', render: (r) => <span className="font-mono text-xs">{r.cnic || '—'}</span> },
  { key: 'familyHead', label: 'Family Head', wrap: true, render: (r) => r.familyHead || '—' },
  {
    // Two records on one number can still be typed differently. Showing the raw value next to the
    // normalised one makes that visible instead of hiding it behind the match.
    key: 'phoneAsEntered', label: 'As Entered',
    render: (r) => <span className="font-mono text-xs text-gray-500">{r.phoneAsEntered}</span>,
  },
  { key: 'registeredOn', label: 'Registered' },
];

const ACTION_BUTTON = 'px-2 py-1 rounded-lg text-xs font-medium text-emerald-700 hover:bg-emerald-50 whitespace-nowrap';

const DuplicatePatientsReport = ({ hasPermission }) => {
  const drill = useDrill();
  const prompt = usePrompt();
  const notify = useToast();
  const [filter, setFilter] = useState('all');
  // A merge retires a file, so it takes the same permission as deleting one.
  const canMerge = typeof hasPermission === 'function' ? hasPermission('PATIENTS', 'DELETE') : false;
  const [mergePair, setMergePair] = useState(null);

  const fetcher = useCallback(() => reportsAPI.getDuplicatePatients(), []);
  const { data, loading, reload } = useReport(fetcher);

  const allRows = useMemo(() => data?.rows || [], [data]);
  const warned = useMemo(() => data?.warned || [], [data]);
  const cleared = useMemo(() => data?.cleared || [], [data]);
  const merged = useMemo(() => data?.merged || [], [data]);
  const mobiles = useMemo(() => data?.mobiles || [], [data]);
  const mobileSummary = data?.mobileSummary || {};
  const warnedSummary = data?.warnedSummary || {};
  // The mobile cap counts numbers, so the "showing N of M" line has to count numbers too.
  const shownMobileNumbers = useMemo(() => new Set(mobiles.map((m) => m.phone)).size, [mobiles]);

  // Counts describe the loaded worklist, not the uncapped totals; the KPI strip carries those.
  const counts = useMemo(
    () => FILTERS.reduce((acc, f) => ({ ...acc, [f.key]: allRows.filter(f.match).length }), {}),
    [allRows]
  );

  const rows = useMemo(
    () => allRows.filter((FILTERS.find((f) => f.key === filter) || FILTERS[0]).match),
    [allRows, filter]
  );

  // Someone with both files in front of them says these are two people: the pair leaves the
  // report, and the decision is listed at the bottom so it can be undone.
  const clearPair = useCallback(async (r) => {
    const note = await prompt({
      title: 'Not the same person?',
      subtitle: `${r.aPatient} (${r.aMrn}) and ${r.bPatient} (${r.bMrn})`,
      message: 'The pair leaves this report until someone restores it. A word on why helps the next reader.',
      placeholder: 'e.g. twins, or cousins with the same name',
      confirmText: 'Clear pair',
      required: false,
      multiline: true,
    });
    if (note === null) return;
    try {
      await patientAPI.clearLookalikePair(r.aId, r.bId, note);
      notify('success', 'Pair cleared from the report');
      reload();
    } catch (e) {
      notify('error', e?.message || 'Could not clear the pair');
    }
  }, [prompt, notify, reload]);

  const restorePair = useCallback(async (r) => {
    try {
      await patientAPI.restoreLookalikePair(r.decisionId);
      notify('success', 'Pair is back on the report');
      reload();
    } catch (e) {
      notify('error', e?.message || 'Could not restore the pair');
    }
  }, [notify, reload]);

  // The action columns stop the click so it does not also open the row's drill-down.
  const columns = useMemo(() => [
    ...COLUMNS,
    {
      key: 'actions', label: '',
      render: (r) => (
        <span className="flex items-center gap-1">
          {canMerge && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMergePair(r); }}
              className="px-2 py-1 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
            >
              Merge
            </button>
          )}
          <button type="button" onClick={(e) => { e.stopPropagation(); clearPair(r); }} className={ACTION_BUTTON}>
            Not the same person
          </button>
        </span>
      ),
      csv: () => '',
    },
  ], [clearPair, canMerge]);
  const clearedColumns = useMemo(() => [
    ...CLEARED_COLUMNS,
    {
      key: 'actions', label: '',
      render: (r) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); restorePair(r); }} className={ACTION_BUTTON}>
          Restore
        </button>
      ),
      csv: () => '',
    },
  ], [restorePair]);

  const k = data?.kpis || {};
  const kpis = [
    { label: 'Candidate Pairs', value: k.candidatePairs ?? 0, sub: 'Across the whole patient base' },
    { label: 'Patients Involved', value: k.patientsInvolved ?? 0, sub: 'Files appearing in a pair' },
    {
      label: 'Confirmed',
      value: k.confirmed ?? 0,
      sub: 'Same CNIC or B-Form',
      tone: Number(k.confirmed || 0) > 0 ? 'alert' : undefined,
    },
    { label: 'High', value: k.high ?? 0, sub: 'Name plus a second identifier' },
    { label: 'Medium', value: k.medium ?? 0, sub: 'One identifier, weaker corroboration' },
    { label: 'Low', value: k.low ?? 0, sub: 'Name alone' },
  ];

  const asOf = data?.range?.to || 'today';

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <p className="text-sm text-gray-500 flex-1 min-w-[300px]">
          As of {asOf}. Every live patient record is compared against every other, with no date
          range, because two registrations of the same person are usually months or years apart.
        </p>
        <PrintButton />
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <div className="pt-2">
        <p className="text-sm text-gray-500">
          {warnedSummary.registrations ?? 0} registrations went ahead after the form warned that the
          person might already be on file ({warnedSummary.warnings ?? 0} warnings in all).{' '}
          {warnedSummary.stillFlagged ?? 0} still look like the file they were warned about today,{' '}
          {warnedSummary.confirmed ?? 0} now share an identifier with it, and{' '}
          {warnedSummary.cleared ?? 0} were cleared as two different people.
        </p>
      </div>

      <ReportTable
        onRowClick={(r) => (r.newDeleted || r.matchDeleted ? null : drill.open({
          type: 'duplicatePair',
          mrn: r.newMrn,
          mrn2: r.matchMrn,
          rules: rulesOf(r).map(ruleLabel).join(', '),
        }))}
        title="Registered after a warning"
        columns={WARNED_COLUMNS}
        rows={warned}
        csvName={`registered-after-warning-${asOf}`}
        emptyText="No registration has gone ahead after a warning yet"
        footnote={'"Today" judges the pair again under the rules of this report as both files stand now. A pair that still matches is one to look at; one that no longer matches was corrected since, or was never the same person.'}
      />

      {data?.truncated && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800">
          Showing the {allRows.length} strongest of {k.candidatePairs} candidate pairs. The figures
          above count all of them.
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-1 inline-flex flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filter === f.key ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {f.label}
            <span className={`ml-1.5 ${filter === f.key ? 'text-navy-200' : 'text-gray-400'}`}>
              {counts[f.key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({
          type: 'duplicatePair',
          mrn: r.aMrn,
          mrn2: r.bMrn,
          // Carried through so the modal can say why these two were paired, rather than leaving the
          // reader to infer it from a field list that plays no part in the matching.
          rules: rulesOf(r).map(ruleLabel).join(', '),
        })}
        title="Candidate duplicate records"
        columns={columns}
        rows={rows}
        csvName={`duplicate-patients-${asOf}`}
        emptyText="No candidate duplicates at this confidence"
        footnote={'"Primary" is the record with more visit history, which is usually the one worth keeping. A Confirmed pair shares a CNIC or B-Form outright; the Shared CNIC report shows those same records grouped by the ID they share. "Merge" folds one file into the other with both in front of you; "Not the same person" takes a pair off this list. Both decisions are kept below.'}
      />

      <div className="pt-2">
        <p className="text-sm text-gray-500">
          {mobileSummary.numbers ?? 0} mobile numbers are on more than one patient record, covering{' '}
          {mobileSummary.patients ?? 0} records, and the largest has {mobileSummary.largestGroup ?? 0}{' '}
          under it. Sharing a number is normal within a household, so treat this as context rather
          than a duplicate signal on its own. {mobileSummary.repeatedName ?? 0} of them carry the
          same name more than once, and those usually are duplicates.
          {data?.mobilesTruncated && (
            <span className="text-amber-700">
              {' '}Showing the {shownMobileNumbers} most notable of {mobileSummary.numbers} numbers.
            </span>
          )}
        </p>
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'phone', value: r.phone })}
        title="Patients sharing a mobile number"
        columns={MOBILE_COLUMNS}
        rows={mobiles}
        csvName={`shared-mobile-numbers-${asOf}`}
        emptyText="No mobile number is on more than one patient record"
        footnote="Numbers are matched on their last 10 digits, so the same number typed two ways still groups. Rows are ordered by the numbers most worth a look first."
      />

      <ReportTable
        onRowClick={(r) => (r.aDeleted || r.bDeleted ? null : drill.open({ type: 'duplicatePair', mrn: r.aMrn, mrn2: r.bMrn }))}
        title="Pairs cleared as two different people"
        columns={clearedColumns}
        rows={cleared}
        csvName={`cleared-pairs-${asOf}`}
        emptyText="No pair has been cleared"
        footnote="A cleared pair is left out of the candidate list above. Restore puts it back."
      />

      <ReportTable
        title="Files merged"
        columns={MERGED_COLUMNS}
        rows={merged}
        csvName={`merged-files-${asOf}`}
        emptyText="No files have been merged"
        footnote="A retired MRN still finds the kept file in search, so an old slip resolves. Nothing here is undone from the screen; the counts say what would have to move back."
      />

      <ReportPrintLayout
        title="Potential Duplicate Patients"
        subtitle={`Whole patient base, as of ${asOf}`}
        kpis={kpis}
        tables={[
          { title: 'Registered after a warning', columns: WARNED_COLUMNS, rows: warned },
          { title: 'Candidate duplicate records', columns: COLUMNS, rows },
          { title: 'Patients sharing a mobile number', columns: MOBILE_COLUMNS, rows: mobiles },
          { title: 'Pairs cleared as two different people', columns: CLEARED_COLUMNS, rows: cleared },
          { title: 'Files merged', columns: MERGED_COLUMNS, rows: merged },
        ]}
      />

      {mergePair && (
        <MergePatientsDialog
          pair={mergePair}
          onClose={() => setMergePair(null)}
          onMerged={() => { setMergePair(null); reload(); }}
        />
      )}

      {drill.node}
    </div>
  );
};

export default DuplicatePatientsReport;
